import { useEffect, useState } from 'react';
import moment from 'moment';
import {
	BellIcon,
	BellSlashIcon,
	ArrowPathIcon,
	ClockIcon,
	ExclamationTriangleIcon,
	CheckCircleIcon,
	XCircleIcon,
	CalendarIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { DataTable } from '../../components/nextgen';
import Modal from '../../components/Shell/Modal';
import Button from '../../components/Button';

import useAsync from '../../hooks/useAsync';

import {
	getNotificationStatus,
	toggleNotifications,
	getNotificationLogs,
	getScheduledNotifications,
	getPredictedNotifications,
	getFailedNotifications,
	getNotificationStats,
	retryNotification,
	sendPredictedNotification,
	previewNotificationRecipients,
} from '../../api/admin';

const TABS = [
	{ id: 'sent', name: 'Sent', icon: CheckCircleIcon },
	{ id: 'scheduled', name: 'Scheduled', icon: ClockIcon },
	{ id: 'predicted', name: 'Predicted', icon: CalendarIcon },
	{ id: 'failed', name: 'Failed', icon: XCircleIcon },
];

const getStatusColor = (status) => {
	switch (status) {
		case 'sent':
			return 'bg-green-100 text-green-800';
		case 'pending':
			return 'bg-yellow-100 text-yellow-800';
		case 'failed':
			return 'bg-red-100 text-red-800';
		case 'predicted':
			return 'bg-blue-100 text-blue-800';
		default:
			return 'bg-gray-100 text-gray-800';
	}
};

const getEventTypeColor = (eventKey) => {
	if (eventKey?.startsWith('contract'))
		return 'bg-purple-100 text-purple-800';
	if (eventKey?.startsWith('ticket')) return 'bg-blue-100 text-blue-800';
	if (eventKey?.startsWith('work_order'))
		return 'bg-orange-100 text-orange-800';
	if (eventKey?.startsWith('pm_schedule'))
		return 'bg-teal-100 text-teal-800';
	return 'bg-gray-100 text-gray-800';
};

const NotificationDetailModal = ({ notification, open, onClose, onRetry, onSendPredicted }) => {
	const [recipients, setRecipients] = useState([]);
	const [loadingRecipients, setLoadingRecipients] = useState(false);

	// Fetch recipients when modal opens for predicted notifications
	useEffect(() => {
		if (open && notification?.type === 'predicted' && notification?.organization?.id) {
			setLoadingRecipients(true);
			previewNotificationRecipients({
				eventKey: notification.eventKey,
				entityType: notification.entityType,
				entityId: notification.entityId,
				organizationId: notification.organization.id,
			})
				.then((res) => {
					setRecipients(res?.data?.recipients || []);
				})
				.catch((err) => {
					console.error('Failed to load recipients:', err);
					setRecipients([]);
				})
				.finally(() => {
					setLoadingRecipients(false);
				});
		} else {
			setRecipients([]);
		}
	}, [open, notification]);

	if (!notification) return null;

	const isPredicted = notification.type === 'predicted';

	return (
		<Modal open={open} onClose={onClose} title="Notification Details" size="3xl">
			<div className="space-y-6 mt-4">
				{/* Header Info */}
				<div className="flex items-center gap-4">
					<span
						className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${getStatusColor(notification.status || notification.type)}`}
					>
						{notification.status || notification.type}
					</span>
					<span
						className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${getEventTypeColor(notification.eventKey)}`}
					>
						{notification.eventKey}
					</span>
					<span className="text-gray-500 text-sm">
						{(notification.createdAt || notification.predictedDate) &&
							moment(notification.createdAt || notification.predictedDate).format('MMM D, YYYY h:mm:ss A')}
					</span>
				</div>

				{/* Scheduled to Send - for predicted notifications */}
				{isPredicted && notification.scheduledToSendAt && (
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Scheduled to Send
							</label>
							<div className="bg-gray-50 rounded-md p-3 text-sm">
								{(() => {
									const sendDate = moment(notification.scheduledToSendAt);
									const isPast = sendDate.isBefore(moment());
									return (
										<span className={isPast ? 'text-green-600 font-medium' : ''}>
											{isPast ? 'Ready to send now' : sendDate.format('MMM D, YYYY')}
											{!isPast && ` (${sendDate.fromNow()})`}
										</span>
									);
								})()}
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Due Date
							</label>
							<div className="bg-gray-50 rounded-md p-3 text-sm">
								{moment(notification.predictedDate).format('MMM D, YYYY')}
								{notification.reminderDays > 0 && (
									<span className="text-gray-500 ml-2">
										({notification.reminderDays} day reminder)
									</span>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Recipients - for predicted notifications */}
				{isPredicted && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Recipients ({recipients.length})
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm max-h-32 overflow-y-auto">
							{loadingRecipients ? (
								<span className="text-gray-400">Loading recipients...</span>
							) : recipients.length > 0 ? (
								<div className="space-y-1">
									{recipients.map((r) => (
										<div key={r.id} className="flex items-center gap-2">
											<span className="text-gray-900">
												{r.firstName} {r.lastName}
											</span>
											<span className="text-gray-500">({r.email})</span>
										</div>
									))}
								</div>
							) : (
								<span className="text-gray-400">No recipients found</span>
							)}
						</div>
					</div>
				)}

				{/* Recipient - for sent/failed notifications */}
				{!isPredicted && (
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Recipient
							</label>
							<div className="bg-gray-50 rounded-md p-3 text-sm">
								{notification.recipientEmail || (
									<span className="text-gray-400">-</span>
								)}
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Channel
							</label>
							<div className="bg-gray-50 rounded-md p-3 text-sm">
								{notification.channel || 'email'}
							</div>
						</div>
					</div>
				)}

				{/* Entity Info */}
				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Entity Type
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm">
							{notification.entityType || '-'}
						</div>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Entity ID
						</label>
						<div className="bg-gray-50 rounded-md p-3 font-mono text-sm">
							{notification.entityId || '-'}
						</div>
					</div>
				</div>

				{/* Organization */}
				{notification.organization && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Organization
						</label>
						<div className="bg-gray-50 rounded-md p-3 text-sm">
							{notification.organization.name}
						</div>
					</div>
				)}

				{/* Error Message (for failed) */}
				{notification.errorMessage && (
					<div>
						<label className="block text-sm font-medium text-gray-500 mb-1">
							Error Message
						</label>
						<div className="bg-red-50 text-red-700 rounded-md p-3 text-sm">
							{notification.errorMessage}
						</div>
					</div>
				)}

				{/* Template Data */}
				{notification.templateData &&
					Object.keys(notification.templateData).length > 0 && (
						<div>
							<label className="block text-sm font-medium text-gray-500 mb-1">
								Template Data
							</label>
							<pre className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs overflow-auto max-h-48">
								{JSON.stringify(notification.templateData, null, 2)}
							</pre>
						</div>
					)}

				{/* Retry Button for failed */}
				{notification.status === 'failed' && onRetry && (
					<div className="flex justify-end pt-4 border-t">
						<Button
							onClick={() => onRetry(notification.id)}
							className="inline-flex items-center"
						>
							<ArrowPathIcon className="h-4 w-4 mr-2" />
							Retry Notification
						</Button>
					</div>
				)}

				{/* Send Now Button for predicted */}
				{notification.type === 'predicted' && onSendPredicted && (
					<div className="flex justify-end pt-4 border-t">
						<Button
							onClick={() => onSendPredicted(notification)}
							className="inline-flex items-center"
						>
							<BellIcon className="h-4 w-4 mr-2" />
							Send Now
						</Button>
					</div>
				)}
			</div>
		</Modal>
	);
};

const NotificationsAdmin = () => {
	const [activeTab, setActiveTab] = useState('sent');
	const [page, setPage] = useState(1);
	const [selectedNotification, setSelectedNotification] = useState(null);
	const [isToggling, setIsToggling] = useState(false);

	// Fetch notification status (global toggle)
	const {
		value: statusData,
		execute: refreshStatus,
		status: statusLoading,
	} = useAsync(getNotificationStatus, { immediate: true });

	// Fetch stats
	const { value: statsData, execute: refreshStats } = useAsync(
		getNotificationStats,
		{ immediate: true },
	);

	// Fetch logs (sent notifications)
	const {
		value: logsData,
		execute: refreshLogs,
		status: logsLoading,
	} = useAsync(getNotificationLogs, { immediate: false });

	// Fetch scheduled
	const {
		value: scheduledData,
		execute: refreshScheduled,
		status: scheduledLoading,
	} = useAsync(getScheduledNotifications, { immediate: false });

	// Fetch predicted
	const {
		value: predictedData,
		execute: refreshPredicted,
		status: predictedLoading,
	} = useAsync(getPredictedNotifications, { immediate: false });

	// Fetch failed
	const {
		value: failedData,
		execute: refreshFailed,
		status: failedLoading,
	} = useAsync(getFailedNotifications, { immediate: false });

	// Retry notification
	const { execute: doRetry, status: retryStatus } = useAsync(retryNotification, {
		immediate: false,
	});

	// Send predicted notification
	const { execute: doSendPredicted } = useAsync(sendPredictedNotification, {
		immediate: false,
	});

	// Load data based on active tab
	useEffect(() => {
		setPage(1);
		loadTabData();
	}, [activeTab]);

	useEffect(() => {
		if (activeTab === 'sent') {
			refreshLogs({ offset: (page - 1) * 25, limit: 25 });
		}
	}, [page]);

	const loadTabData = () => {
		switch (activeTab) {
			case 'sent':
				refreshLogs({ offset: 0, limit: 25 });
				break;
			case 'scheduled':
				refreshScheduled({ limit: 100 });
				break;
			case 'predicted':
				refreshPredicted({ days: 30 });
				break;
			case 'failed':
				refreshFailed({ limit: 100 });
				break;
		}
	};

	const handleToggle = async () => {
		setIsToggling(true);
		try {
			await toggleNotifications(!statusData?.data?.enabled);
			await refreshStatus();
		} catch (err) {
			console.error('Failed to toggle notifications:', err);
		} finally {
			setIsToggling(false);
		}
	};

	const handleRetry = async (id) => {
		try {
			await doRetry(id);
			setSelectedNotification(null);
			loadTabData();
			refreshStats();
		} catch (err) {
			console.error('Failed to retry notification:', err);
		}
	};

	const handleSendPredicted = async (notification) => {
		try {
			await doSendPredicted({
				eventKey: notification.eventKey,
				entityType: notification.entityType,
				entityId: notification.entityId,
				organizationId: notification.organization?.id,
			});
			setSelectedNotification(null);
			loadTabData();
			refreshStats();
		} catch (err) {
			console.error('Failed to send predicted notification:', err);
		}
	};

	const isLoading =
		(activeTab === 'sent' && logsLoading === 'pending') ||
		(activeTab === 'scheduled' && scheduledLoading === 'pending') ||
		(activeTab === 'predicted' && predictedLoading === 'pending') ||
		(activeTab === 'failed' && failedLoading === 'pending');

	const getTabData = () => {
		switch (activeTab) {
			case 'sent':
				return logsData?.data?.logs || [];
			case 'scheduled':
				return scheduledData?.data || [];
			case 'predicted':
				return predictedData?.data || [];
			case 'failed':
				return failedData?.data || [];
			default:
				return [];
		}
	};

	const columns = [
		{
			key: 'timestamp',
			header: 'Time',
			width: '160px',
			sortable: false,
			render: (row) => (
				<div>
					<div className="text-gray-900 text-sm">
						{moment(row.createdAt || row.predictedDate).format('MMM D, YYYY')}
					</div>
					<div className="text-gray-500 text-xs">
						{moment(row.createdAt || row.predictedDate).format('h:mm A')}
					</div>
				</div>
			),
		},
		{
			key: 'status',
			header: 'Status',
			width: '100px',
			sortable: false,
			render: (row) => (
				<span
					className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(row.status || row.type)}`}
				>
					{row.status || row.type}
				</span>
			),
		},
		{
			key: 'eventKey',
			header: 'Event',
			width: '180px',
			sortable: false,
			render: (row) => (
				<span
					className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getEventTypeColor(row.eventKey)}`}
				>
					{row.eventKey}
				</span>
			),
		},
		{
			key: 'entity',
			header: 'Entity',
			sortable: false,
			render: (row) => (
				<div className="max-w-sm">
					<div className="text-gray-900 text-sm truncate">
						{row.entityName || row.entityType || '-'}
					</div>
					{row.daysUntil !== undefined && (
						<div
							className={`text-xs ${row.daysUntil < 0 ? 'text-red-600' : row.daysUntil <= 7 ? 'text-orange-600' : 'text-gray-500'}`}
						>
							{row.daysUntil < 0
								? `${Math.abs(row.daysUntil)} days overdue`
								: row.daysUntil === 0
									? 'Due today'
									: `${row.daysUntil} days left`}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'recipient',
			header: 'Recipient',
			width: '200px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-700 text-sm truncate">
					{row.recipientEmail || '-'}
				</div>
			),
		},
		{
			key: 'organization',
			header: 'Organization',
			width: '160px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-700 text-sm truncate">
					{row.organization?.name || '-'}
				</div>
			),
		},
	];

	// Add retry column for failed tab
	const failedColumns = [
		...columns,
		{
			key: 'actions',
			header: '',
			width: '100px',
			sortable: false,
			render: (row) => (
				<button
					onClick={(e) => {
						e.stopPropagation();
						handleRetry(row.id);
					}}
					className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
				>
					<ArrowPathIcon className="h-3 w-3 mr-1" />
					Retry
				</button>
			),
		},
	];

	// Add scheduled to send and send now columns for predicted tab
	const predictedColumns = [
		// Replace the timestamp column with scheduledToSendAt for predicted
		{
			key: 'scheduledToSendAt',
			header: 'Scheduled to Send',
			width: '160px',
			sortable: false,
			render: (row) => {
				const sendDate = moment(row.scheduledToSendAt);
				const isPast = sendDate.isBefore(moment());
				const isToday = sendDate.isSame(moment(), 'day');
				return (
					<div>
						<div className={`text-sm ${isPast ? 'text-green-600 font-medium' : isToday ? 'text-orange-600' : 'text-gray-900'}`}>
							{isPast ? 'Ready to send' : sendDate.format('MMM D, YYYY')}
						</div>
						{!isPast && (
							<div className="text-gray-500 text-xs">
								{isToday ? 'Today' : sendDate.fromNow()}
							</div>
						)}
						{row.reminderDays > 0 && (
							<div className="text-gray-400 text-xs">
								{row.reminderDays} day reminder
							</div>
						)}
					</div>
				);
			},
		},
		{
			key: 'status',
			header: 'Status',
			width: '100px',
			sortable: false,
			render: (row) => (
				<span
					className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(row.status || row.type)}`}
				>
					{row.status || row.type}
				</span>
			),
		},
		{
			key: 'eventKey',
			header: 'Event',
			width: '180px',
			sortable: false,
			render: (row) => (
				<span
					className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getEventTypeColor(row.eventKey)}`}
				>
					{row.eventKey}
				</span>
			),
		},
		{
			key: 'entity',
			header: 'Entity',
			sortable: false,
			render: (row) => (
				<div className="max-w-sm">
					<div className="text-gray-900 text-sm truncate">
						{row.entityName || row.entityType || '-'}
					</div>
					{row.predictedDate && (
						<div className="text-gray-500 text-xs">
							Due: {moment(row.predictedDate).format('MMM D, YYYY')}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'organization',
			header: 'Organization',
			width: '160px',
			sortable: false,
			render: (row) => (
				<div className="text-gray-700 text-sm truncate">
					{row.organization?.name || '-'}
				</div>
			),
		},
		{
			key: 'actions',
			header: '',
			width: '100px',
			sortable: false,
			render: (row) => (
				<button
					onClick={(e) => {
						e.stopPropagation();
						handleSendPredicted(row);
					}}
					className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
				>
					<BellIcon className="h-3 w-3 mr-1" />
					Send Now
				</button>
			),
		},
	];

	return (
		<MainContent fullWidth maxWidth="w-full">
			<div className="flex items-center justify-between mb-4">
				<PageHeading divider={false}>Notifications</PageHeading>

				{/* Global Toggle */}
				<div className="flex items-center gap-4">
					{statusData?.data?.enabled === false && (
						<div className="flex items-center text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
							<ExclamationTriangleIcon className="h-5 w-5 mr-2" />
							<span className="text-sm font-medium">
								Notifications are disabled
							</span>
						</div>
					)}
					<button
						onClick={handleToggle}
						disabled={isToggling || statusLoading === 'pending'}
						className={`inline-flex items-center px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
							statusData?.data?.enabled
								? 'bg-green-100 text-green-800 hover:bg-green-200'
								: 'bg-red-100 text-red-800 hover:bg-red-200'
						} ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
					>
						{statusData?.data?.enabled ? (
							<>
								<BellIcon className="h-5 w-5 mr-2" />
								{isToggling ? 'Disabling...' : 'Enabled'}
							</>
						) : (
							<>
								<BellSlashIcon className="h-5 w-5 mr-2" />
								{isToggling ? 'Enabling...' : 'Disabled'}
							</>
						)}
					</button>
				</div>
			</div>

			{/* Stats Cards */}
			{statsData?.data && (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
					<div className="bg-white border border-gray-200 rounded-lg p-4">
						<div className="text-sm text-gray-500">Sent</div>
						<div className="text-2xl font-semibold text-green-600">
							{statsData.data.byStatus?.sent || 0}
						</div>
					</div>
					<div className="bg-white border border-gray-200 rounded-lg p-4">
						<div className="text-sm text-gray-500">Pending</div>
						<div className="text-2xl font-semibold text-yellow-600">
							{statsData.data.byStatus?.pending || 0}
						</div>
					</div>
					<div className="bg-white border border-gray-200 rounded-lg p-4">
						<div className="text-sm text-gray-500">Failed</div>
						<div className="text-2xl font-semibold text-red-600">
							{statsData.data.byStatus?.failed || 0}
						</div>
					</div>
					<div className="bg-white border border-gray-200 rounded-lg p-4">
						<div className="text-sm text-gray-500">Email Channel</div>
						<div className="text-2xl font-semibold text-gray-900">
							{statsData.data.byChannel?.email || 0}
						</div>
					</div>
				</div>
			)}

			{/* Tabs */}
			<div className="border-b border-gray-200 mb-4">
				<nav className="-mb-px flex space-x-8">
					{TABS.map((tab) => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id)}
							className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
								activeTab === tab.id
									? 'border-blue-500 text-blue-600'
									: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
							}`}
						>
							<tab.icon className="h-5 w-5 mr-2" />
							{tab.name}
							{tab.id === 'failed' && (statsData?.data?.byStatus?.failed || 0) > 0 && (
								<span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
									{statsData.data.byStatus.failed}
								</span>
							)}
						</button>
					))}
				</nav>
			</div>

			{/* Data Table */}
			<div className="border border-gray-200 rounded-lg overflow-hidden">
				<DataTable
					data={getTabData()}
					columns={
						activeTab === 'failed'
							? failedColumns
							: activeTab === 'predicted'
								? predictedColumns
								: columns
					}
					loading={isLoading}
					pagination={activeTab === 'sent'}
					serverSidePagination={activeTab === 'sent'}
					currentPage={page}
					totalItems={logsData?.data?.total || 0}
					onPageChange={setPage}
					pageSize={25}
					searchable
					searchPlaceholder="Search notifications..."
					searchFields={['eventKey', 'recipientEmail', 'entityName']}
					emptyTitle={`No ${activeTab} notifications`}
					emptyDescription={
						activeTab === 'predicted'
							? 'No upcoming notifications predicted for the next 30 days'
							: `No ${activeTab} notifications found`
					}
					hoverable
					bordered={false}
					onRowClick={(row) => setSelectedNotification(row)}
				/>
			</div>

			{/* Notification Detail Modal */}
			<NotificationDetailModal
				notification={selectedNotification}
				open={!!selectedNotification}
				onClose={() => setSelectedNotification(null)}
				onRetry={activeTab === 'failed' ? handleRetry : null}
				onSendPredicted={activeTab === 'predicted' ? handleSendPredicted : null}
			/>
		</MainContent>
	);
};

export default NotificationsAdmin;
